import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Mail,
  Send,
  Users,
} from "lucide-react";
import { adminApi, isAdminLoggedIn, verifyAdminToken } from "@/utils/auth";

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({ total: 0 });

  useEffect(() => {
    const checkAuth = async () => {
      if (!isAdminLoggedIn()) {
        navigate("/admin/login");
        return;
      }
      const valid = await verifyAdminToken();
      if (!valid) {
        navigate("/admin/login");
        return;
      }
      fetchStats();
    };
    checkAuth();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await adminApi.get("/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please enter both subject and message");
      return;
    }

    setSending(true);
    toast.info("Sending emails... This may take a moment.");
    
    try {
      // Use longer timeout for broadcast (120 seconds) since it sends to all registrants
      const response = await adminApi.post("/broadcast", {
        subject: subject.trim(),
        message: message.trim(),
      }, { timeout: 120000 });
      
      toast.success(`Email sent to ${response.data.sent} registrants!`);
      
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} emails failed to send`);
      }
      
      // Clear form
      setSubject("");
      setMessage("");
    } catch (error) {
      console.error("Broadcast error:", error);
      // Check if it's a timeout - emails might still have been sent
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning("Request timed out, but emails may have been sent. Check your inbox.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send broadcast");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-overlay">
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="text-muted-foreground hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3">
              <Mail className="w-8 h-8 text-primary" />
              Broadcast Email
            </h1>
            <p className="text-muted-foreground">
              Send an email to all registered users
            </p>
          </div>
        </div>

        {/* Recipients Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="glass border-primary/30 bg-primary/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-primary" />
                <div>
                  <p className="text-white font-bold">
                    {stats.total} Registrant{stats.total !== 1 ? "s" : ""} will receive this email
                  </p>
                  <p className="text-muted-foreground text-sm">
                    Email will be sent from noreply@socajumbies.com
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Compose Email</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Subject</Label>
                <Input
                  placeholder="e.g., Important Update - Jouvert 2026"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="bg-muted/50 border-transparent focus:border-primary h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Message</Label>
                <Textarea
                  placeholder="Write your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-muted/50 border-transparent focus:border-primary min-h-[200px] resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  The message will be personalized with each registrant's name automatically.
                </p>
              </div>

              {/* Preview */}
              {(subject || message) && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Preview</Label>
                  <div className="bg-[#1a1a2e] p-6 rounded-lg border border-white/10">
                    <div className="text-center mb-4">
                      <h2 className="text-primary font-bold text-xl">SOCA JUMBIES</h2>
                      <p className="text-muted-foreground text-sm">Jouvert 2026</p>
                    </div>
                    <p className="text-muted-foreground mb-4">Hi [Registrant Name],</p>
                    <div className="bg-[#2a2a4e] p-4 rounded-lg mb-4">
                      <p className="text-white whitespace-pre-wrap">{message || "Your message will appear here..."}</p>
                    </div>
                    <p className="text-muted-foreground text-xs text-center">
                      Soca Jumbies Jouvert Band - See you on the road! 🎉
                    </p>
                  </div>
                </div>
              )}

              {/* Send Button */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="w-full h-12 bg-primary hover:bg-primary/90 text-black font-bold"
                    disabled={!subject.trim() || !message.trim() || sending || stats.total === 0}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {sending ? "Sending..." : `Send to ${stats.total} Registrant${stats.total !== 1 ? "s" : ""}`}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-background border-white/10">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">Send Broadcast Email?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will send an email to all {stats.total} registered users. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-white/10">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={sendBroadcast}
                      className="bg-primary hover:bg-primary/90 text-black"
                    >
                      Send Email
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
